import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import rateTableService from '@api/v1/rateTableService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'

/**
 * CC-53132: what a read that produced no answer is allowed to render.
 *
 * Never the empty state. 'blocked' and 'failed' are the two ways a read ends with
 * nothing to show that are NOT "you have none of these", and on a rate-table screen
 * the difference is money: "no rate tables" and "we never asked" look identical, and
 * only one of them means the firm bills at the default rate.
 *
 * `role="alert"` because the panel around it already looks like an answer, and a
 * retry because a blocked read never resolves on its own — react-query paused it, so
 * `queryFn` was never called and nothing will call it until something asks again.
 */
const ReadNotice = ({ state, title, children, onRetry }) => {
  const blocked = state === 'blocked'
  return (
    <div
      role="alert"
      className={`m-4 rounded-lg border p-4 text-sm ${
        blocked ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-100 bg-red-50 text-red-700'
      }`}
    >
      <p className="font-medium">{title}</p>
      <p className="mt-1">{children}</p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-3 rounded-lg border bg-white px-3 py-1.5 text-xs font-medium ${
          blocked
            ? 'border-amber-300 text-amber-900 hover:bg-amber-100'
            : 'border-red-200 text-red-700 hover:bg-red-100'
        }`}
      >
        Try again
      </button>
    </div>
  )
}

const RateTables = () => {
  useDocumentTitle('Rate Tables')
  const [activeTab, setActiveTab] = useState('tables')
  const [selectedTableId, setSelectedTableId] = useState(null)
  const [showPastVersions, setShowPastVersions] = useState(true)

  const tablesQuery = useQuery({
    queryKey: ['rate-tables'],
    queryFn: () => rateTableService.getRateTables(),
  })

  const timekeepersQuery = useQuery({
    queryKey: ['rate-timekeepers'],
    queryFn: () => rateTableService.getTimekeepers(),
  })

  const detailQuery = useQuery({
    queryKey: ['rate-table-detail', selectedTableId],
    queryFn: () => rateTableService.getRateTable(selectedTableId),
    enabled: !!selectedTableId,
  })

  const versionsQuery = useQuery({
    queryKey: ['rate-table-versions', selectedTableId, showPastVersions],
    queryFn: () => rateTableService.getVersions(selectedTableId, showPastVersions),
    enabled: !!selectedTableId,
  })

  const usageQuery = useQuery({
    queryKey: ['rate-table-usage', selectedTableId],
    queryFn: () => rateTableService.getUsage(selectedTableId),
    enabled: !!selectedTableId,
  })

  /*
   * CC-53132 — five reads on this page, and four of them had no failure surface at
   * all. `isLoading` is `isPending && isFetching`, so it is FALSE for a query that is
   * pending but NOT in flight: above all `fetchStatus: 'paused'`, where react-query's
   * default `networkMode: 'online'` refuses to fire while `onlineManager` believes the
   * browser is offline. `queryFn` is never called, nothing throws, nothing logs, and
   * `data` stays `undefined` — which every line below defaulted to `[]`.
   *
   * Rendered, that was: "0 tables", "No rate tables found", and a timekeeper table
   * with a header row and nothing under it reading "0 entries". A firm reading that
   * concludes it has no rate tables — i.e. that everyone bills at their standard
   * rate — which is a billing decision made from a screen that never asked the
   * server anything.
   *
   * `useReadState` rather than the bare function so each read carries the permanent
   * blocked-read diagnostic; a paused fetch leaves no other trace by construction.
   */
  const tablesState = useReadState('rate-tables', tablesQuery)
  const timekeepersState = useReadState('rate-timekeepers', timekeepersQuery)
  const detailState = useReadState('rate-table-detail', detailQuery)
  const versionsState = useReadState('rate-table-versions', versionsQuery)
  const usageState = useReadState('rate-table-usage', usageQuery)

  // Coerced only for the ready branches that iterate them; the STATE, never the
  // length, decides whether a count or an empty state may be spoken aloud.
  const tables = Array.isArray(tablesQuery.data?.data) ? tablesQuery.data.data : []
  const timekeepers = Array.isArray(timekeepersQuery.data?.data) ? timekeepersQuery.data.data : []
  const detail = detailQuery.data?.data

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Rate Tables</h1>
        <p className="text-sm text-gray-500 mt-1">Manage billing rates for timekeepers, matters, and clients</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {[{ id: 'tables', label: 'Rate Tables' }, { id: 'timekeepers', label: 'Timekeepers & Billing Groups' }].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'tables' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Rate Tables List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 lg:sticky lg:top-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Rate Tables</h2>
            {/* A count is a claim. "0 tables" for a read that never happened is the
                same defect as a wrong figure, so an unresolved read counts nothing
                and says so with an em dash. */}
            <p className="text-xs text-gray-500 mt-0.5">
              {tablesState === 'ready'
                ? `${tables.length} table${tables.length !== 1 ? 's' : ''}`
                : '—'}
            </p>
          </div>
          <div className="divide-y divide-gray-50">
            {/* 'idle' rides with 'loading' here on purpose: this query has no
                `enabled` option, so it cannot reach idle at all. It is covered
                anyway because every branch below names a state, and an unhandled
                fifth one renders NOTHING — a blank panel for whoever adds an
                `enabled` gate later. That was a real miss on the first version of
                this fix elsewhere in the sweep, not a hypothetical. */}
            {tablesState === 'loading' || tablesState === 'idle' ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner message="Loading rate tables..." />
              </div>
            ) : tablesState === 'blocked' ? (
              <ReadNotice
                state="blocked"
                title="Can’t reach the server, so we haven’t loaded your rate tables."
                onRetry={() => tablesQuery.refetch()}
              >
                This is not a firm with no rate tables — the list was never read. Your
                connection looks offline; check it and try again.
              </ReadNotice>
            ) : tablesState === 'failed' ? (
              <ReadNotice
                state="failed"
                title="Failed to load rate tables"
                onRetry={() => tablesQuery.refetch()}
              >
                This is not a firm with no rate tables — nothing was read, so no rate on
                this screen is describing how you bill.
              </ReadNotice>
            ) : tables.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 text-center">No rate tables found</p>
            ) : (
              tables.map((table) => (
                <button
                  key={table.id}
                  onClick={() => setSelectedTableId(table.id)}
                  className={`w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors ${
                    selectedTableId === table.id ? 'bg-cb-light border-l-2 border-cb-accent' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{table.name}</p>
                    {table.isInUse && (
                      <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-medium">In Use</span>
                    )}
                  </div>
                  {table.effectiveDate && (
                    <p className="text-xs text-gray-500 mt-0.5">Effective: {table.effectiveDate}</p>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Rate Table Detail */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">
                {detail ? (detail.Name || detail.name || 'Rate Table Detail') : 'Select a Rate Table'}
              </h2>
            </div>
            <div className="p-5">
              {/* `!selectedTableId` IS this query's 'idle' branch, and it was always
                  written: the detail query is `enabled: !!selectedTableId`, the same
                  predicate. So idle already has correct copy — "not asked yet", said
                  in the user's terms — and must not be folded into loading (there is
                  no request to spin for) nor dressed as a failure. Everything after
                  this point therefore runs with the query enabled, which is why the
                  branches below never test for idle again. */}
              {!selectedTableId ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  Select a rate table from the list to view details
                </p>
              ) : detailState === 'loading' ? (
                <div className="py-8 flex justify-center">
                  <LoadingSpinner message="Loading detail..." />
                </div>
              ) : detailState === 'blocked' || detailState === 'failed' ? (
                <ReadNotice
                  state={detailState}
                  title={detailState === 'blocked'
                    ? 'Can’t reach the server, so we haven’t loaded this rate table.'
                    : `Failed to load this rate table: ${detailQuery.error?.message}`}
                  onRetry={() => detailQuery.refetch()}
                >
                  This is not an empty rate table — nothing was read, so the rates it
                  holds are unknown rather than absent.
                </ReadNotice>
              ) : detail ? (
                <RateTableDetail
                  detail={detail}
                  versions={{
                    state: versionsState,
                    rows: Array.isArray(versionsQuery.data?.data) ? versionsQuery.data.data : [],
                    refetch: () => versionsQuery.refetch(),
                  }}
                  usage={{
                    state: usageState,
                    rows: Array.isArray(usageQuery.data?.data) ? usageQuery.data.data : [],
                    refetch: () => usageQuery.refetch(),
                  }}
                  showPastVersions={showPastVersions}
                  setShowPastVersions={setShowPastVersions}
                />
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">Could not load details</p>
              )}
            </div>
          </div>
        </div>
      </div>}

      {activeTab === 'timekeepers' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Timekeepers & Billing Groups</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {timekeepersState === 'ready' ? `${timekeepers.length} entries` : '—'}
            </p>
          </div>
          {/* This read had NO loading, error or empty handling whatsoever: it went
              straight to `timekeepers.map`, so a paused or failed read rendered a
              table with a header row and no body — visually identical to a firm with
              no timekeepers, and captioned "0 entries". The ready branch below is
              unchanged, including a genuinely empty table for a genuinely empty
              answer; only the three unresolved states are new. 'idle' rides with
              'loading' because this query has no `enabled` option and cannot reach
              it — see the same note on the rate-table list above. */}
          {timekeepersState === 'loading' || timekeepersState === 'idle' ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner message="Loading timekeepers..." />
            </div>
          ) : timekeepersState === 'blocked' || timekeepersState === 'failed' ? (
            <ReadNotice
              state={timekeepersState}
              title={timekeepersState === 'blocked'
                ? 'Can’t reach the server, so we haven’t loaded your timekeepers.'
                : `Failed to load timekeepers: ${timekeepersQuery.error?.message}`}
              onRetry={() => timekeepersQuery.refetch()}
            >
              This is not a firm with no timekeepers and no billing groups — the list was
              never read, so every rate that would appear here is unknown, not missing.
            </ReadNotice>
          ) : (
            <div className="p-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Name</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Rate</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {timekeepers.map((tk, i) => (
                    <tr key={i} className="hover:bg-gray-50/50">
                      <td className="py-2 pr-4 text-gray-900">{tk.name || '—'}</td>
                      <td className="py-2 px-3 text-right text-gray-900 font-medium">
                        {tk.rate ? `$${Number(tk.rate).toFixed(2)}` : '—'}
                      </td>
                      <td className="py-2 px-3 text-gray-600">
                        {tk.isGroup ? 'Group' : 'User'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * `versions` and `usage` arrive as `{ state, rows, refetch }` rather than bare arrays.
 *
 * They used to be arrays, and both sections rendered on `length > 0` — so an
 * unresolved read made the whole section VANISH. That is the same lie as an empty
 * state, told more quietly: a rate table showing no "Used By" section reads as a rate
 * table nothing uses, which is exactly the fact someone checks before editing or
 * deleting it. Absence has to be something the read actually reported.
 */
const RateTableDetail = ({ detail, versions, usage, showPastVersions, setShowPastVersions }) => {
  const entries = detail.Entries || detail.entries || []
  const currentVersion = detail.CurrentVersion || detail.currentVersion

  // Both queries are `enabled: !!selectedTableId`, and this component only mounts
  // once a detail for that same id has resolved — so 'idle' cannot be reached here
  // and folding it in with 'loading' cannot produce a spinner that never stops. It is
  // handled rather than left to fall through, so no state renders nothing at all.
  const pending = (s) => s === 'loading' || s === 'idle'
  const unreadable = (s) => s === 'blocked' || s === 'failed'

  return (
    <div className="space-y-6">
      {/* Current Version Info */}
      {currentVersion && (
        <div className="flex items-center gap-4 text-sm">
          <div>
            <span className="text-gray-500">Effective:</span>{' '}
            <span className="text-gray-900 font-medium">{currentVersion.StartDate || currentVersion.startDate || '—'}</span>
          </div>
        </div>
      )}

      {/* Rate Entries */}
      {entries.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Rate Entries ({entries.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Timekeeper</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entries.map((e, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="py-2 pr-4 text-gray-900">{e.TimekeeperName || e.Name || e.name || `Entry ${i + 1}`}</td>
                    <td className="py-2 px-3 text-right font-medium text-gray-900">
                      ${(e.Rate ?? e.rate ?? 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Versions. The count in the heading is gated with the section it heads —
          they are the same claim written twice. */}
      {versions.state === 'ready' && versions.rows.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">
              {showPastVersions ? 'Past' : 'Future'} Versions ({versions.rows.length})
            </h3>
            <button
              onClick={() => setShowPastVersions(!showPastVersions)}
              className="text-xs text-cb-accent hover:text-cb-navy font-medium"
            >
              Show {showPastVersions ? 'future' : 'past'} versions
            </button>
          </div>
          <div className="space-y-2">
            {versions.rows.map((v, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">{v.StartDate || v.startDate || 'No date'}</span>
                <span className="text-xs text-gray-500">{v.EntryCount || v.entryCount || 0} entries</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {pending(versions.state) && (
        <p className="text-xs text-gray-500">Loading version history…</p>
      )}
      {unreadable(versions.state) && (
        <ReadNotice
          state={versions.state}
          title="We couldn’t read this rate table’s version history."
          onRetry={versions.refetch}
        >
          A rate table with no past or future versions looks exactly like this, so treat
          it as unknown: the effective dates below and above today were never fetched.
        </ReadNotice>
      )}

      {/* Usage */}
      {usage.state === 'ready' && usage.rows.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Used By ({usage.rows.length})</h3>
          <div className="flex flex-wrap gap-2">
            {usage.rows.map((u, i) => (
              <span key={i} className="inline-flex px-2.5 py-1 rounded-full bg-cb-light text-xs text-cb-navy font-medium">
                {u.Name || u.name || u.ObjectName || 'Unknown'}
              </span>
            ))}
          </div>
        </div>
      )}
      {pending(usage.state) && (
        <p className="text-xs text-gray-500">Checking what uses this rate table…</p>
      )}
      {unreadable(usage.state) && (
        <ReadNotice
          state={usage.state}
          title="We couldn’t read what uses this rate table."
          onRetry={usage.refetch}
        >
          Do not read this as "nothing uses it" — that is what an unused rate table looks
          like too, and it is the difference between a safe edit and a repricing.
        </ReadNotice>
      )}

      {/* Fallback: raw data */}
      {entries.length === 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Raw Data</h3>
          <pre className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 overflow-x-auto max-h-64">
            {JSON.stringify(detail, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

export default RateTables
